import { Container, Label, SelectInput, TextAreaInput, TextInput } from '@playcanvas/pcui';

import { Events } from '../events';

type CaseType = 'accident' | 'premises' | 'crime-scene' | 'construction' | 'general';

type CaseMetadata = {
    caseType?: CaseType;
    caseNumber?: string;
    caseName?: string;
    incidentDate?: string;
    preparedBy?: string;
    disclaimers?: string[];
};

class CasePanel extends Container {
    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'case-panel',
            class: 'panel',
            hidden: true
        };

        super(args);

        this.style.top = '50%';
        this.style.transform = 'translate(0, -50%)';
        this.style.right = '102px';
        this.style.width = '320px';
        this.style.flexDirection = 'column';

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const header = new Container({
            class: 'panel-header'
        });

        const icon = new Label({
            text: '\uE1B1',
            class: 'panel-header-icon'
        });

        const label = new Label({
            text: 'Case',
            class: 'panel-header-label'
        });

        header.append(icon);
        header.append(label);

        const makeRow = (labelText: string) => {
            const row = new Container({
                class: 'view-panel-row'
            });

            const rowLabel = new Label({
                text: labelText,
                class: 'view-panel-row-label'
            });

            row.append(rowLabel);
            return row;
        };

        const caseTypeRow = makeRow('Case Type');
        const caseTypeInput = new SelectInput({
            class: 'view-panel-row-select',
            options: [
                { v: '', t: '—' },
                { v: 'accident', t: 'Accident' },
                { v: 'premises', t: 'Premises' },
                { v: 'crime-scene', t: 'Crime Scene' },
                { v: 'construction', t: 'Construction' },
                { v: 'general', t: 'General' }
            ],
            defaultValue: ''
        });
        caseTypeInput.dom.id = 'case-panel-case-type';
        const caseTypeNativeInput = caseTypeInput.dom.querySelector('select') as HTMLSelectElement | null;
        if (caseTypeNativeInput) {
            caseTypeNativeInput.id = 'case-panel-case-type-input';
        }
        caseTypeRow.append(caseTypeInput);

        const caseNumberRow = makeRow('Case Number');
        const caseNumberInput = new TextInput({ class: 'view-panel-row-select' });
        caseNumberInput.dom.id = 'case-panel-case-number';
        const caseNumberNativeInput = caseNumberInput.dom.querySelector('input') as HTMLInputElement | null;
        if (caseNumberNativeInput) {
            caseNumberNativeInput.id = 'case-panel-case-number-input';
        }
        caseNumberRow.append(caseNumberInput);

        const caseNameRow = makeRow('Case Name');
        const caseNameInput = new TextInput({ class: 'view-panel-row-select' });
        caseNameInput.dom.id = 'case-panel-case-name';
        const caseNameNativeInput = caseNameInput.dom.querySelector('input') as HTMLInputElement | null;
        if (caseNameNativeInput) {
            caseNameNativeInput.id = 'case-panel-case-name-input';
        }
        caseNameRow.append(caseNameInput);

        const incidentDateRow = makeRow('Incident Date');
        const incidentDateInput = new TextInput({
            class: 'view-panel-row-select',
            placeholder: 'YYYY-MM-DD'
        });
        incidentDateInput.dom.id = 'case-panel-incident-date';
        const incidentDateNativeInput = incidentDateInput.dom.querySelector('input') as HTMLInputElement | null;
        if (incidentDateNativeInput) {
            incidentDateNativeInput.id = 'case-panel-incident-date-input';
        }
        incidentDateRow.append(incidentDateInput);

        const preparedByRow = makeRow('Prepared By');
        const preparedByInput = new TextInput({ class: 'view-panel-row-select' });
        preparedByInput.dom.id = 'case-panel-prepared-by';
        const preparedByNativeInput = preparedByInput.dom.querySelector('input') as HTMLInputElement | null;
        if (preparedByNativeInput) {
            preparedByNativeInput.id = 'case-panel-prepared-by-input';
        }
        preparedByRow.append(preparedByInput);

        const disclaimersRow = makeRow('Disclaimers');
        disclaimersRow.style.height = '86px';
        disclaimersRow.style.alignItems = 'stretch';
        const disclaimersInput = new TextAreaInput({ class: 'view-panel-row-select' });
        disclaimersInput.style.height = '80px';
        disclaimersInput.dom.id = 'case-panel-disclaimers';
        const disclaimersNativeInput = disclaimersInput.dom.querySelector('textarea') as HTMLTextAreaElement | null;
        if (disclaimersNativeInput) {
            disclaimersNativeInput.id = 'case-panel-disclaimers-input';
        }
        disclaimersRow.append(disclaimersInput);

        this.append(header);
        this.append(caseTypeRow);
        this.append(caseNumberRow);
        this.append(caseNameRow);
        this.append(incidentDateRow);
        this.append(preparedByRow);
        this.append(disclaimersRow);

        let applyingMetadata = false;

        const setVisible = (visible: boolean) => {
            if (visible === this.hidden) {
                if (visible) {
                    events.fire('viewPanel.setVisible', false);
                    events.fire('colorPanel.setVisible', false);
                }
                this.hidden = !visible;
                events.fire('casePanel.visible', visible);
            }
        };

        const emitPatch = (patch: Partial<CaseMetadata>) => {
            if (applyingMetadata) {
                return;
            }
            events.fire('case.setMetadata', patch);
        };

        const parseCaseType = (value: unknown): CaseType | undefined => {
            const text = typeof value === 'string' ? value : '';
            if (text === 'accident' || text === 'premises' || text === 'crime-scene' || text === 'construction' || text === 'general') {
                return text;
            }
            return undefined;
        };

        const normalizeText = (value: unknown) => {
            if (typeof value !== 'string') {
                return undefined;
            }
            const trimmed = value.trim();
            return trimmed.length > 0 ? trimmed : undefined;
        };

        const updateUi = (metadata: CaseMetadata | undefined) => {
            applyingMetadata = true;

            caseTypeInput.value = metadata?.caseType ?? '';
            caseNumberInput.value = metadata?.caseNumber ?? '';
            caseNameInput.value = metadata?.caseName ?? '';
            incidentDateInput.value = metadata?.incidentDate ?? '';
            preparedByInput.value = metadata?.preparedBy ?? '';
            disclaimersInput.value = Array.isArray(metadata?.disclaimers) ? metadata!.disclaimers!.join('\n') : '';

            applyingMetadata = false;
        };

        caseTypeInput.on('change', () => {
            emitPatch({
                caseType: parseCaseType(caseTypeInput.value)
            });
        });

        caseNumberInput.on('change', () => {
            emitPatch({
                caseNumber: normalizeText(caseNumberInput.value)
            });
        });

        caseNameInput.on('change', () => {
            emitPatch({
                caseName: normalizeText(caseNameInput.value)
            });
        });

        incidentDateInput.on('change', () => {
            emitPatch({
                incidentDate: normalizeText(incidentDateInput.value)
            });
        });

        preparedByInput.on('change', () => {
            emitPatch({
                preparedBy: normalizeText(preparedByInput.value)
            });
        });

        disclaimersInput.on('change', () => {
            const disclaimerText = String(disclaimersInput.value ?? '');
            const lines = disclaimerText.split('\n').map(line => line.trim()).filter(Boolean);

            emitPatch({
                disclaimers: lines.length > 0 ? lines : undefined
            });
        });

        events.on('case.metadataChanged', (metadata: CaseMetadata | undefined) => {
            updateUi(metadata);
        });

        events.function('casePanel.visible', () => {
            return !this.hidden;
        });

        events.on('casePanel.setVisible', (visible: boolean) => {
            setVisible(visible);
        });

        events.on('casePanel.toggleVisible', () => {
            setVisible(this.hidden);
        });

        events.on('viewPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        events.on('colorPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        updateUi(events.invoke('case.metadata') as CaseMetadata | undefined);
    }
}

export { CasePanel };
